/* eslint-disable no-redeclare */
import {
  cleanInputCreate,
  cleanInputUpdate,
  createIntegration as createMutation,
  CreateIntegrationInput,
  CreateIntegrationMutationVariables,
  deleteIntegration as deleteMutation,
  DeleteIntegrationMutationVariables,
  getIntegration as getIntegrationQuery,
  getReadId,
  getTableClientId,
  Integration,
  IntegrationName,
  isConfigurationTrue,
  isIntegrationActive,
  LooseObject,
  onCreateIntegration,
  OnCreateIntegrationSubscription,
  onDeleteIntegration,
  OnDeleteIntegrationSubscription,
  onUpdateIntegration,
  OnUpdateIntegrationSubscription,
  revokePontoIntegration as revokeIntegrationMutation,
  RevokePontoIntegrationMutation,
  RevokePontoIntegrationMutationVariables,
  syncIntegrations,
  updateIntegration as updateMutation,
  UpdateIntegrationInput,
  verifyPontoIntegration as verifyIntegrationMutation,
  VerifyPontoIntegrationInput,
  VerifyPontoIntegrationMutation,
  VerifyPontoIntegrationMutationVariables,
  DateTime,
  getDateTimeDifference,
  todayDateTime,
} from '@rentguru/commons-utils';
import {
  cleanSyncQueries,
  deleteEntityWithFetchBefore,
  list,
  mutation,
  NUMBER_OF_MINUTES_FOR_REFETCH,
  useSubscriptions,
} from '@up2rent/graphql';
import isNil from 'lodash/isNil';
import React, { Reducer, useContext, useEffect, useMemo, useReducer, useRef } from 'react';

import { fetchAuthSession } from 'src/auth/session';
import { getContactSyncUrl, getGoogleConnectUrl, getMicrosoftConnectUrl } from 'src/hooks/utils/cloudIntegrations';

import { useUser } from './UserContext';
import { usePermissions } from './utils/PermissionsContext';

export interface IntegrationContext extends IntegrationState {
  createIntegration: (
    input: CreateIntegrationInput | Omit<CreateIntegrationInput, 'clientId' | 'readId'>
  ) => Promise<Integration>;
  getIntegration: (id: string) => Integration | undefined;
  getIntegrationByName: (name: IntegrationName) => Integration | undefined;
  updateIntegration: (updates: UpdateIntegrationInput) => Promise<Integration>;
  deleteIntegration: (integration: Integration) => Promise<Integration | null>;
  setFetchIntegrations: () => void;
  fetchAndSetIntegrations: () => Promise<void>;
  verifyPontoIntegration: (
    input: Omit<VerifyPontoIntegrationInput, 'clientId'>
  ) => Promise<VerifyPontoIntegrationMutation>;
  revokePontoIntegration: () => Promise<RevokePontoIntegrationMutation>;
  verifyMicrosoftIntegration: (code: string) => Promise<number>;
  verifyGoogleIntegration: (code: string, redirectUri: string) => Promise<number>;
  forceSyncContacts: () => Promise<void>;
  isActiveIntegrationConfiguration: (integrationName: IntegrationName, configurationName: string) => boolean;
}

interface IntegrationState {
  integrations: Integration[] | null;
  error: string | undefined;
  loading: boolean;
  shouldFetch: boolean;
  lastFetch: DateTime | null;
}

type Action =
  | {
      type: 'SHOULD_FETCH' | 'IS_FETCHING';
      payload?: { forceFetch?: boolean };
    }
  | {
      type: 'ADD_INTEGRATION';
      payload: { integration: Integration };
    }
  | {
      type: 'FETCHED';
      payload: { integrations: Integration[] };
    }
  | {
      type: 'FETCH_FAILED';
      payload: { error: string };
    }
  | {
      type: 'UPDATE_INTEGRATION';
      payload: { integration: Integration };
    }
  | {
      type: 'DELETE_INTEGRATION';
      payload: { id: string };
    };

const initialState: IntegrationState = {
  loading: false,
  error: undefined,
  integrations: null,
  shouldFetch: false,
  lastFetch: null,
};

const integrationReducer = (state: IntegrationState, action: Action): IntegrationState => {
  switch (action.type) {
    case 'SHOULD_FETCH':
      // Check //lastFetchForAgencyRates TODO
      if (
        !action.payload?.forceFetch &&
        (state.shouldFetch ||
          state.loading ||
          (state.lastFetch &&
            getDateTimeDifference({ start: state.lastFetch, end: todayDateTime() }, 'minutes') <
              NUMBER_OF_MINUTES_FOR_REFETCH))
      ) {
        return state;
      }
      return {
        ...state,
        shouldFetch: true,
      };
    case 'IS_FETCHING':
      return {
        ...state,
        loading: true,
      };
    case 'FETCHED':
      // Build List with merged object
      return {
        ...state,
        loading: false,
        shouldFetch: false,
        integrations: action.payload.integrations,
        lastFetch: todayDateTime(),
        error: undefined,
      };
    case 'FETCH_FAILED':
      return {
        ...state,
        loading: false,
        shouldFetch: false,
        error: action.payload.error,
      };
    case 'ADD_INTEGRATION':
      return {
        ...state,
        integrations: state.integrations?.some((integration) => integration.id === action.payload.integration.id)
          ? state.integrations
          : [...(state.integrations ?? []), action.payload.integration],
      };
    case 'UPDATE_INTEGRATION':
      if (!state.integrations) {
        return state;
      }
      return {
        ...state,
        integrations: state.integrations.map((integration) => {
          if (integration.id === action.payload.integration.id) {
            return action.payload.integration;
          }
          return integration;
        }),
      };
    case 'DELETE_INTEGRATION':
      if (!state.integrations) {
        return state;
      }
      return {
        ...state,
        integrations: state.integrations.filter((integration) => integration.id !== action.payload.id),
      };
    default:
      return state;
  }
};

export const fetchIntegrations = async (
  clientId: string,
  permissions: { settingsAutomationWrite: boolean },
  additionalFilter?: LooseObject
): Promise<Integration[]> => {
  const query = permissions.settingsAutomationWrite ? syncIntegrations : cleanSyncQueries(syncIntegrations);
  return await list<Integration>(query, 'clientId', clientId, additionalFilter);
};

export const IntegrationContext = React.createContext<IntegrationContext | null>(null);

export const IntegrationContextProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer<Reducer<IntegrationState, Action>>(integrationReducer, initialState);
  const { clientId, userId } = useUser();
  const { settingsAutomationWrite } = usePermissions();

  const setFetchIntegrations = () => {
    dispatch({ type: 'SHOULD_FETCH' });
  };

  useEffect(() => {
    if (state.shouldFetch) fetchAndSetIntegrations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.shouldFetch]);

  useSubscriptions<OnCreateIntegrationSubscription, OnUpdateIntegrationSubscription, OnDeleteIntegrationSubscription>(
    onCreateIntegration,
    onUpdateIntegration,
    onDeleteIntegration,
    (data) => {
      dispatch({
        type: 'ADD_INTEGRATION',
        payload: { integration: data.onCreateIntegration as Integration },
      });
    },
    (data) => {
      dispatch({
        type: 'UPDATE_INTEGRATION',
        payload: { integration: data.onUpdateIntegration as Integration },
      });
    },
    (data) => {
      const { id } = data.onDeleteIntegration as Integration;
      dispatch({
        type: 'DELETE_INTEGRATION',
        payload: { id },
      });
    }
  );

  const createIntegration = async (
    input: CreateIntegrationInput | Omit<CreateIntegrationInput, 'clientId' | 'readId'>
  ): Promise<Integration> => {
    let integration: Integration | undefined;
    try {
      integration = await mutation<Integration, CreateIntegrationMutationVariables>(createMutation, {
        input: {
          ...(cleanInputCreate(input) as CreateIntegrationInput),
          clientId: getTableClientId(clientId!, 'Integration'),
          readId: getReadId(clientId!, 'Integration'),
        },
      });
      dispatch({ type: 'ADD_INTEGRATION', payload: { integration } });
      dispatch({ type: 'SHOULD_FETCH', payload: { forceFetch: true } });
    } catch (error) {
      state.error = error as string;
    }
    return integration!;
  };

  const updateIntegration = async (updates: UpdateIntegrationInput): Promise<Integration> => {
    let integration: Integration | undefined;
    try {
      integration = await mutation<Integration>(updateMutation, {
        input: { ...cleanInputUpdate(updates, false) },
      });
      if (integration) {
        dispatch({ type: 'UPDATE_INTEGRATION', payload: { integration } });
        dispatch({ type: 'SHOULD_FETCH', payload: { forceFetch: true } });
      }
    } catch (error) {
      state.error = error as string;
    }
    return integration!;
  };

  const deleteIntegration = async (integration: Integration): Promise<Integration | null> => {
    let deletedIntegration: Integration | null | undefined;
    try {
      deletedIntegration = await deleteEntityWithFetchBefore<Integration, DeleteIntegrationMutationVariables>(
        integration,
        getIntegrationQuery,
        deleteMutation
      );

      dispatch({ type: 'DELETE_INTEGRATION', payload: { id: integration.id } });
      return deletedIntegration;
    } catch (error) {
      state.error = error as string;
    }
    return deletedIntegration!;
  };

  const getIntegration = (id: string) => {
    const integration = (state.integrations ?? []).find((integrationElement) => integrationElement.id === id);
    return integration;
  };

  const getIntegrationByName = (name: IntegrationName) => {
    const integration = (state.integrations ?? []).find((integrationElement) => integrationElement.name === name);
    return integration;
  };
  const fetchAndSetIntegrations = async () => {
    try {
      dispatch({ type: 'IS_FETCHING' });
      const integrations = await fetchIntegrations(getTableClientId(clientId!, 'Integration'), {
        settingsAutomationWrite,
      });
      dispatch({ type: 'FETCHED', payload: { integrations } });
    } catch (error) {
      console.error('[integrations] fetching integrations failed', error);
      dispatch({
        type: 'FETCH_FAILED',
        payload: { error: error instanceof Error ? error.message : String(error) },
      });
    }
  };

  const verifyPontoIntegration = async (
    input: Omit<VerifyPontoIntegrationInput, 'clientId'>
  ): Promise<VerifyPontoIntegrationMutation> => {
    const response = await mutation<VerifyPontoIntegrationMutation, VerifyPontoIntegrationMutationVariables>(
      verifyIntegrationMutation,
      {
        input: {
          ...(cleanInputCreate(input) as VerifyPontoIntegrationInput),
          clientId: clientId!,
        },
      }
    );
    return response;
  };

  const verifyMicrosoftIntegration = async (code: string) => {
    const authSession = await fetchAuthSession();
    const idToken = authSession.tokens?.idToken?.toString();
    const syncUrl = new URL(getMicrosoftConnectUrl());
    syncUrl.searchParams.set('code', code);
    const result = await fetch(syncUrl.href, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
    });
    return result.status;
  };

  const verifyGoogleIntegration = async (code: string, redirectUri: string) => {
    const authSession = await fetchAuthSession();
    const idToken = authSession.tokens?.idToken?.toString();
    const syncUrl = new URL(getGoogleConnectUrl());
    syncUrl.searchParams.set('code', code);
    syncUrl.searchParams.set('redirect_uri', redirectUri);
    const result = await fetch(syncUrl.href, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
    });
    return result.status;
  };

  const revokePontoIntegration = async (): Promise<RevokePontoIntegrationMutation> => {
    const response = await mutation<RevokePontoIntegrationMutation, RevokePontoIntegrationMutationVariables>(
      revokeIntegrationMutation,
      {
        input: {
          userId: userId!,
          clientId: clientId!,
        },
      }
    );
    return response;
  };

  const isActiveIntegrationConfiguration = (integrationName: IntegrationName, configurationName: string): boolean => {
    const integration = getIntegrationByName(integrationName) ?? null;
    return Boolean(
      isIntegrationActive(integration) && isConfigurationTrue(configurationName, integration?.configurations)
    );
  };

  const forceSyncContacts = async () => {
    const authSession = await fetchAuthSession();
    const idToken = authSession.tokens?.idToken?.toString();
    const syncUrl = getContactSyncUrl();
    const response = await fetch(syncUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
    });
    if (!response.ok) {
      throw new Error('Failed to force sync contacts');
    }
  };

  const values = useMemo(
    () => ({
      ...state,
      createIntegration,
      getIntegration,
      getIntegrationByName,
      updateIntegration,
      deleteIntegration,
      setFetchIntegrations,
      fetchAndSetIntegrations,
      verifyPontoIntegration,
      revokePontoIntegration,
      verifyMicrosoftIntegration,
      verifyGoogleIntegration,
      isActiveIntegrationConfiguration,
      forceSyncContacts,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state]
  );

  return <IntegrationContext.Provider value={values}>{children}</IntegrationContext.Provider>;
};

export const useIntegrations = (): IntegrationContext => {
  const firstRender = useRef<boolean>(false);
  const context = useContext<IntegrationContext | null>(IntegrationContext);

  if (isNil(context)) {
    throw new Error('`useIntegrations` hook must be used within a `IntegrationContext` component');
  }
  useEffect(() => {
    if (!firstRender.current && isNil(context.integrations)) {
      context.setFetchIntegrations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstRender]);

  return context;
};
